import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MOST_VISITED_FILTERS, NEWS_CONFIG } from "../config/newsConfig";
import { fetchMostVisitedNewsBatch } from "../api/newsApi";

const createEmptyMostVisitedPages = () =>
  MOST_VISITED_FILTERS.reduce((pages, filter) => {
    pages[filter] = [];
    return pages;
  }, {});

const createEmptyFilterState = (defaultValue) =>
  MOST_VISITED_FILTERS.reduce((state, filter) => {
    state[filter] = defaultValue;
    return state;
  }, {});

export const useMostVisitedNews = ({
  activeSources,
  availableSourcesLength = 0,
  focusSource,
  isMostVisitedMode,
  isMobile = false,
}) => {
  const [mostVisitedPages, setMostVisitedPages] = useState(
    createEmptyMostVisitedPages
  );
  const [mostVisitedLoadingByFilter, setMostVisitedLoadingByFilter] = useState(
    () => createEmptyFilterState(false)
  );
  const [mostVisitedErrorsByFilter, setMostVisitedErrorsByFilter] = useState(
    () => createEmptyFilterState("")
  );
  const [mostVisitedTarget, setMostVisitedTarget] = useState(null);
  const [mostVisitedFilter, setMostVisitedFilter] = useState("24h");

  const areSourceSelectionsReady =
    activeSources !== null && availableSourcesLength > 0;

  const mostVisitedLoadedRef = useRef(new Set());
  const inFlightRequestsRef = useRef(new Map());
  const requestVersionsRef = useRef({});

  const isDesktopFocusTickerMode = Boolean(
    !isMobile && focusSource && !isMostVisitedMode
  );

  const isDesktopFocusMostVisitedMode = Boolean(
    !isMobile && focusSource && isMostVisitedMode
  );

  const mostVisitedSources = useMemo(() => {
    /*
    | Desktop Focus owns a source-scoped Most Visited dataset even before the
    | full Most Visited view opens. This lets the visible Focus ticker load
    | only the selected source's 24-hour items.
    |
    | Mobile keeps its previous behavior until Most Visited is explicitly
    | opened, because its Focus header does not render the desktop news ticker.
    */
    if ((!isMobile && focusSource) || (isMostVisitedMode && focusSource)) {
      return [focusSource];
    }

    if (!activeSources || activeSources.length === 0) return [];

    if (
      availableSourcesLength > 0 &&
      activeSources.length === availableSourcesLength
    ) {
      return [];
    }

    return activeSources;
  }, [
    activeSources,
    availableSourcesLength,
    focusSource,
    isMobile,
    isMostVisitedMode,
  ]);

  const mostVisitedSourcesKey = useMemo(() => {
    return mostVisitedSources.length > 0
      ? mostVisitedSources.join("||")
      : "all";
  }, [mostVisitedSources]);

  const abortAllMostVisitedRequests = useCallback(() => {
    inFlightRequestsRef.current.forEach(({ controller }) => {
      controller.abort();
    });

    inFlightRequestsRef.current.clear();
  }, []);

  useEffect(() => {
    abortAllMostVisitedRequests();

    mostVisitedLoadedRef.current = new Set();
    requestVersionsRef.current = {};

    setMostVisitedPages(createEmptyMostVisitedPages());
    setMostVisitedLoadingByFilter(createEmptyFilterState(false));
    setMostVisitedErrorsByFilter(createEmptyFilterState(""));
  }, [abortAllMostVisitedRequests, mostVisitedSourcesKey]);

  useEffect(() => {
    return () => {
      abortAllMostVisitedRequests();
    };
  }, [abortAllMostVisitedRequests]);

  const fetchMostVisitedNews = useCallback(
    async (filter, { force = false } = {}) => {
      const safeFilter = MOST_VISITED_FILTERS.includes(filter)
        ? filter
        : "24h";
      const cacheKey = `${safeFilter}|${mostVisitedSourcesKey}`;

      if (!force && mostVisitedLoadedRef.current.has(cacheKey)) {
        return;
      }

      const currentRequest = inFlightRequestsRef.current.get(cacheKey);

      if (currentRequest && !force) {
        return currentRequest.promise;
      }

      if (currentRequest && force) {
        currentRequest.controller.abort();
        inFlightRequestsRef.current.delete(cacheKey);
      }

      const controller = new AbortController();
      const nextVersion = (requestVersionsRef.current[cacheKey] || 0) + 1;

      requestVersionsRef.current[cacheKey] = nextVersion;

      setMostVisitedLoadingByFilter((current) => ({
        ...current,
        [safeFilter]: true,
      }));

      setMostVisitedErrorsByFilter((current) => ({
        ...current,
        [safeFilter]: "",
      }));

      const requestPromise = (async () => {
        try {
          const safeItems = await fetchMostVisitedNewsBatch({
            filter: safeFilter,
            sources: mostVisitedSources,
            limit: NEWS_CONFIG.mostVisitedLimit,
            signal: controller.signal,
          });

          if (
            controller.signal.aborted ||
            requestVersionsRef.current[cacheKey] !== nextVersion
          ) {
            return;
          }

          mostVisitedLoadedRef.current.add(cacheKey);

          setMostVisitedPages((current) => ({
            ...current,
            [safeFilter]: safeItems,
          }));
        } catch (error) {
          if (error?.name === "AbortError") return;

          if (requestVersionsRef.current[cacheKey] !== nextVersion) {
            return;
          }

          setMostVisitedErrorsByFilter((current) => ({
            ...current,
            [safeFilter]: error?.message || "خطای نامشخص",
          }));

          /* Keep the last successful page visible on refresh errors. */
        } finally {
          const activeRequest = inFlightRequestsRef.current.get(cacheKey);

          if (activeRequest?.version === nextVersion) {
            inFlightRequestsRef.current.delete(cacheKey);
          }

          if (requestVersionsRef.current[cacheKey] === nextVersion) {
            setMostVisitedLoadingByFilter((current) => ({
              ...current,
              [safeFilter]: false,
            }));
          }
        }
      })();

      inFlightRequestsRef.current.set(cacheKey, {
        controller,
        promise: requestPromise,
        version: nextVersion,
      });

      return requestPromise;
    },
    [mostVisitedSources, mostVisitedSourcesKey]
  );

  const loadMostVisitedFilters = useCallback(
    (filters, { force = false } = {}) => {
      if (!areSourceSelectionsReady) {
        return Promise.resolve();
      }

      const safeFilters = Array.from(
        new Set(
          (filters || []).filter((filter) =>
            MOST_VISITED_FILTERS.includes(filter)
          )
        )
      );

      return Promise.all(
        safeFilters.map((filter) =>
          fetchMostVisitedNews(filter, { force })
        )
      );
    },
    [areSourceSelectionsReady, fetchMostVisitedNews]
  );

  const loadAllMostVisited = useCallback(
    ({ force = false } = {}) => {
      return loadMostVisitedFilters(MOST_VISITED_FILTERS, { force });
    },
    [loadMostVisitedFilters]
  );

  /*
  |-------------------------------------------------------------------------
  | Context-aware Most Visited request policy
  |-------------------------------------------------------------------------
  |
  | Home / mobile:
  |   Keep the existing three-period preload and ten-minute refresh.
  |
  | Desktop Focus before ticker click:
  |   Load only `24h` for the selected source, because that is the only
  |   dataset rendered by the visible Focus ticker.
  |
  | Desktop Focus after ticker click:
  |   Keep the already-loaded `24h` page and fetch `week` + `month` once as
  |   the full Most Visited view opens. The regular interval then refreshes
  |   all three periods while that view remains open.
  |
  */
  useEffect(() => {
    if (!areSourceSelectionsReady) return undefined;

    const runInitialRequestPolicy = () => {
      if (isDesktopFocusTickerMode) {
        return loadMostVisitedFilters(["24h"], {
          force: false,
        });
      }

      if (isDesktopFocusMostVisitedMode) {
        return Promise.all([
          fetchMostVisitedNews("24h", { force: false }),
          fetchMostVisitedNews("week", { force: true }),
          fetchMostVisitedNews("month", { force: true }),
        ]);
      }

      return loadAllMostVisited({
        force: true,
      });
    };

    const runScheduledRefreshPolicy = () => {
      if (isDesktopFocusTickerMode) {
        return loadMostVisitedFilters(["24h"], {
          force: true,
        });
      }

      return loadAllMostVisited({
        force: true,
      });
    };

    /*
    | Delay only until the current effect cycle finishes. This prevents
    | React StrictMode's development-only mount probe from creating an
    | aborted duplicate batch, while remaining immediate for the user.
    */
    const initialRefreshTimeoutId = window.setTimeout(() => {
      runInitialRequestPolicy().catch(() => {});
    }, 0);

    const refreshIntervalId = window.setInterval(() => {
      runScheduledRefreshPolicy().catch(() => {});
    }, NEWS_CONFIG.mostVisitedRefreshIntervalMs);

    return () => {
      window.clearTimeout(initialRefreshTimeoutId);
      window.clearInterval(refreshIntervalId);
    };
  }, [
    areSourceSelectionsReady,
    fetchMostVisitedNews,
    isDesktopFocusMostVisitedMode,
    isDesktopFocusTickerMode,
    loadAllMostVisited,
    loadMostVisitedFilters,
    mostVisitedSourcesKey,
  ]);

  const handleMostVisitedFilterChange = useCallback(
    (filter) => {
      if (!MOST_VISITED_FILTERS.includes(filter)) return false;

      setMostVisitedTarget(null);
      setMostVisitedFilter(filter);

      return true;
    },
    []
  );

  const activeMostVisitedItems = mostVisitedPages[mostVisitedFilter] || [];
  const activeMostVisitedCacheKey = `${mostVisitedFilter}|${mostVisitedSourcesKey}`;

  const hasLoadedMostVisitedFilter = useCallback(
    (filter) => {
      const safeFilter = MOST_VISITED_FILTERS.includes(filter)
        ? filter
        : "24h";

      return mostVisitedLoadedRef.current.has(
        `${safeFilter}|${mostVisitedSourcesKey}`
      );
    },
    [mostVisitedSourcesKey]
  );

  const hasLoadedActiveMostVisited = mostVisitedLoadedRef.current.has(
    activeMostVisitedCacheKey
  );

  const hasLoadedAllMostVisited = MOST_VISITED_FILTERS.every((filter) =>
    mostVisitedLoadedRef.current.has(`${filter}|${mostVisitedSourcesKey}`)
  );

  const isMostVisitedLoading = MOST_VISITED_FILTERS.some(
    (filter) => mostVisitedLoadingByFilter[filter]
  );

  const isActiveMostVisitedLoading = Boolean(
    mostVisitedLoadingByFilter[mostVisitedFilter]
  );

  const mostVisitedError =
    mostVisitedErrorsByFilter[mostVisitedFilter] || "";

  const priorityMostVisitedTickerNews = useMemo(() => {
    return (
      MOST_VISITED_FILTERS.map(
        (filter) => mostVisitedPages[filter] || []
      ).find((items) => items.length > 0) || []
    );
  }, [mostVisitedPages]);

  return {
    mostVisitedPages,
    mostVisitedLoadingByFilter,
    mostVisitedErrorsByFilter,
    isMostVisitedLoading,
    isActiveMostVisitedLoading,
    mostVisitedError,
    mostVisitedTarget,
    setMostVisitedTarget,
    mostVisitedFilter,
    setMostVisitedFilter,
    handleMostVisitedFilterChange,
    activeMostVisitedItems,
    hasLoadedActiveMostVisited,
    hasLoadedAllMostVisited,
    hasLoadedMostVisitedFilter,
    priorityMostVisitedTickerNews,
  };
};
